import { useState } from "react";
import type { DrillLibrary } from "../data/schema";
import { Button, Screen } from "./ui";
import { approvedMap, setVideoApproved } from "../lib/overrides";

export function VideoReview({
  library,
  onBack,
}: {
  library: DrillLibrary;
  onBack: () => void;
}) {
  const [approved, setApproved] = useState<Record<string, boolean>>(approvedMap());
  const [openId, setOpenId] = useState<string | null>(null);

  const toggle = (id: string) => {
    const next = !isOk(id);
    setVideoApproved(id, next);
    setApproved({ ...approvedMap() });
  };
  const isOk = (id: string) => {
    const d = library.drills.find((x) => x.id === id)!;
    return d.video.verified || approved[id] === true;
  };

  const missing = library.drills.filter((d) => d.video.youtubeId === "");
  const pending = library.drills.filter((d) => d.video.youtubeId !== "" && !isOk(d.id));
  const okCount = library.drills.filter((d) => isOk(d.id)).length;

  return (
    <Screen
      title="Review videos"
      right={
        <button onClick={onBack} className="text-lg font-semibold text-[var(--color-pitch)]">
          Done
        </button>
      }
    >
      <p className="mb-4 text-base text-neutral-600">
        {okCount}/{library.drills.length} approved · {pending.length} to check ·{" "}
        {missing.length} need a video. Watch each clip, confirm it shows the drill, then
        approve. Approvals are saved on this device.
      </p>

      <div className="space-y-3">
        {library.drills.map((d) => {
          const ok = isOk(d.id);
          const empty = d.video.youtubeId === "";
          return (
            <div key={d.id} className="card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-lg font-bold">{d.name}</p>
                  <p className="truncate text-sm text-neutral-500">{d.video.title}</p>
                </div>
                <span
                  className={`shrink-0 rounded-full px-3 py-1 text-sm font-semibold ${
                    empty
                      ? "bg-neutral-200 text-neutral-600"
                      : ok
                        ? "bg-green-100 text-green-700"
                        : "bg-amber-100 text-amber-800"
                  }`}
                >
                  {empty ? "no video" : ok ? "approved" : "to check"}
                </span>
              </div>

              {!empty && (
                <>
                  <div className="mt-3 flex gap-2">
                    <Button
                      variant="ghost"
                      onClick={() => setOpenId(openId === d.id ? null : d.id)}
                    >
                      {openId === d.id ? "Hide" : "Watch"}
                    </Button>
                    <a
                      href={`https://www.youtube.com/watch?v=${d.video.youtubeId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="tap grid place-items-center rounded-2xl border border-[var(--color-line)] px-4 font-semibold"
                    >
                      Open ↗
                    </a>
                    <Button variant={ok ? "amber" : "primary"} className="flex-1" onClick={() => toggle(d.id)}>
                      {ok ? "Approved ✓" : "Approve"}
                    </Button>
                  </div>
                  {openId === d.id && (
                    <div className="relative mt-3 w-full overflow-hidden rounded-xl" style={{ paddingTop: "56.25%" }}>
                      <iframe
                        className="absolute inset-0 h-full w-full"
                        src={`https://www.youtube-nocookie.com/embed/${d.video.youtubeId}?start=${d.video.startSec ?? 0}&rel=0`}
                        title={d.video.title}
                        allowFullScreen
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </Screen>
  );
}
